'use client';

import React, { useState } from 'react';
import { VStack, Text, HStack } from '@skin-seoul/design-system';
import LucideIcon from '@/components/ui/LucideIcon';
import StarIngredientItem from './components/StarIngredientItem';
import StarIngredientsModal from './components/StarIngredientsModal';
import { useProductService, useRegionCurrency, ProductType, productSizeAndPackTextConverter } from '@skin-seoul/service';
import { overlay } from 'overlay-kit';
import SectionContainer from '../../components/SectionContainer';
import { useParams } from 'next/navigation';
import { usePageLogger } from '@/utils/logger';
interface StarIngredientsSectionProps {
  productId: string;
}
const StarIngredientsSection = ({
  productId
}: StarIngredientsSectionProps) => {
  const {
    useGetProductMeta,
    useGetProductsDetail
  } = useProductService();
  const {
    currency
  } = useRegionCurrency();
  const params = useParams();
  const logger = usePageLogger();
  const {
    data: productMeta
  } = useGetProductMeta({
    productId
  });
  const {
    data: productData
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const {
    starIngredient
  } = productMeta.taxonomies;
  const {
    product
  } = productData;
  const [expandedItem, setExpandedItem] = useState<number | null>(0);
  const handleToggle = (index: number) => {
    setExpandedItem(expandedItem === index ? null : index);
  };
  const handleViewAll = () => {
    const representativeProduct = product.type === ProductType.Variable ? product.variations[0] : product;
    const itemPrice = representativeProduct.salePrice ?? representativeProduct.originalPrice;
    const itemVariant = productSizeAndPackTextConverter(representativeProduct) || '';
    logger?.click('star_ingredients_view_all_click', {
      x_fb_cd_content_type: 'product',
      x_fb_cd_content_name: product.name,
      x_fb_cd_content_ids: product.productId,
      value: itemPrice.toString(),
      currency,
      items: [{
        item_id: product.productId,
        item_name: product.name,
        item_brand: product.brand || '',
        item_sku: product.sku,
        price: itemPrice.toString(),
        item_category: product.category?.[0] || '',
        item_category2: product.category?.[1] || '',
        item_variant: itemVariant,
        product_image_url: product.productImageUrls?.[0] || ''
      }],
      item_list_name: 'PDP - Star Ingredients',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      page_type: 'pdp',
      event_time: Date.now()
    });
    overlay.open(({
      isOpen,
      close
    }) => <StarIngredientsModal isOpen={isOpen} onClose={close} starIngredients={starIngredient} />);
  };
  if (starIngredient.length === 0) {
    return null;
  }
  return <SectionContainer headerTitle="Star Ingredients" headerRightContent={<HStack gap={1} alignItems="center" justifyContent="flex-end" cursor="pointer" onClick={handleViewAll}>
          <Text textStyle={{
      base: 'label.small',
      tablet: 'label.medium'
    }} fontWeight="normal" color="neutral.600" textAlign="left" whiteSpace="nowrap">
            View all ({starIngredient.length})
          </Text>
          <LucideIcon name="ChevronRight" size={{
      base: 'md',
      tablet: 'lg'
    }} color="neutral.600" />
        </HStack>} data-sentry-element="SectionContainer" data-sentry-component="StarIngredientsSection" data-sentry-source-file="StarIngredientsSection.tsx">
      <VStack gap={2} width="100%" alignItems="stretch" data-sentry-element="VStack" data-sentry-source-file="StarIngredientsSection.tsx">
        {starIngredient.slice(0, 4).map((ingredient, index) => <StarIngredientItem key={index} name={ingredient.engName} efficacy={ingredient.efficacy} efficacyDescription={ingredient.efficacyDescription} isExpanded={expandedItem === index} onToggle={() => handleToggle(index)} />)}
      </VStack>
    </SectionContainer>;
};
export default StarIngredientsSection;